import React from "react";
//#region types
import { StyleEnums } from "models/enums";
import { TranslationKeys } from "translations";
import { IconDefinitionConstants } from "utils/constants";
import { getCostForwardingStatus } from "./costForwardingMarkingModel";
//#endregion
//#region components
import { Grid } from "@mui/material";
import { ReadOnlyFormControlLayout, StaticInfoMessage } from "components/common";
import StyledButton from "components/common/buttons/styledButton";
import useMenuButtonClick from "components/common/buttons/useMenuButtonClick";
import ReadOnlyField from "components/common/forms/baseFields/readOnlyField";
import DetailsFormSection from "components/common/wintForms/detailsForm/detailsFormSection";
import TextDisplay from "components/common/wintForms/formDisplays/textDisplay";
import FormFieldLayout from "components/common/wintForms/formLayout/formFieldLayout";
import ReadOnlyFormField from "components/common/wintForms/formLayout/readOnlyFormField";
import CostForwardingInvoiceLink from "./costForwardingInvoiceLink";
import { useCostForwardingMarkingContext } from "./costForwardingMarkingContext";
import { getCostForwardingLockedText } from "./costForwardingStatusText";
//#endregion
import { LocalizationHelperInstance } from "utils/helpers";
import cssExports from "theme/styles/main.module.scss";
import clsx from "clsx";

type SectionLayout = "editable" | "genericFormReadOnly" | "wintFormReadOnly";

interface ReadOnlyValueProps {
  name: string;
  label: string;
  value?: string;
  layout: SectionLayout;
}

const ReadOnlyValue: React.FC<ReadOnlyValueProps> = ({ name, label, value, layout }) => {
  if (!value) return <></>;

  if (layout === "wintFormReadOnly") {
    return (
      <Grid item sm={6}>
        <ReadOnlyFormField
          direction="vertical"
          Component={TextDisplay}
          hideMainLabel
          name={name}
          label={label}
          value={value}
        />
      </Grid>
    );
  }

  return (
    <Grid
      item
      xs={layout === "genericFormReadOnly" ? 6 : "auto"}
      className={layout === "genericFormReadOnly" ? cssExports.formControl : undefined}
    >
      <ReadOnlyField label={label}>{value}</ReadOnlyField>
    </Grid>
  );
};

const FullWidthItem: React.FC<React.PropsWithChildren<{ layout: SectionLayout }>> = ({ layout, children }) => {
  if (layout === "wintFormReadOnly") {
    return (
      <Grid item xs={12}>
        <FormFieldLayout direction="vertical" hideMainLabel hideMessageBubble>
          <Grid container className={clsx(cssExports.readOnlyField, cssExports.readOnlyTextField)}>
            {children}
          </Grid>
        </FormFieldLayout>
      </Grid>
    );
  }

  return (
    <Grid item xs={12} className={layout === "genericFormReadOnly" ? cssExports.formControl : undefined}>
      {children}
    </Grid>
  );
};

interface CostForwardingDetailsSectionProps {
  isReadOnlyForm: boolean;
  isGenericForm?: boolean;
}

const CostForwardingDetailsSection: React.FC<CostForwardingDetailsSectionProps> = ({
  isReadOnlyForm,
  isGenericForm,
}) => {
  const { marking, editAction } = useCostForwardingMarkingContext();
  const onMenuItemClick = useMenuButtonClick();
  if (!marking) return <></>;

  const { label } = getCostForwardingStatus(marking);
  const title = LocalizationHelperInstance.getFormattedMessage(TranslationKeys.CostForwarding.Title);
  const layout: SectionLayout = !isReadOnlyForm
    ? "editable"
    : isGenericForm
      ? "genericFormReadOnly"
      : "wintFormReadOnly";

  const items = (
    <>
      {label.key !== "ReadyForForwarding" && (
        <FullWidthItem layout={layout}>
          <StaticInfoMessage displayType={StyleEnums.DisplayType.Info} fullWidth>
            {getCostForwardingLockedText(label)}
            <CostForwardingInvoiceLink marking={marking} />
          </StaticInfoMessage>
        </FullWidthItem>
      )}
      <ReadOnlyValue
        name="costForwardingCustomer"
        label={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Customer)}
        value={marking.customerName}
        layout={layout}
      />
      <ReadOnlyValue
        name="costForwardingDescription"
        label={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Description)}
        value={marking.description}
        layout={layout}
      />
      {editAction && (
        <FullWidthItem layout={layout}>
          <StyledButton size="xs" onClick={(event) => onMenuItemClick(editAction, false, event)}>
            {LocalizationHelperInstance.getFormattedMessage(TranslationKeys.CostForwarding.EditButton)}
          </StyledButton>
        </FullWidthItem>
      )}
    </>
  );

  if (layout === "genericFormReadOnly") {
    return (
      <Grid container className={cssExports.readOnlyForm}>
        <ReadOnlyFormControlLayout label={title} icon={IconDefinitionConstants.WCostForward} inline>
          {items}
        </ReadOnlyFormControlLayout>
      </Grid>
    );
  }

  return (
    <DetailsFormSection header={title} icon={IconDefinitionConstants.WCostForward} readonly={isReadOnlyForm}>
      {layout === "wintFormReadOnly" ? (
        items
      ) : (
        <FormFieldLayout direction="horizontal" label={title} childGridSize hideMessageBubble>
          <Grid container direction="row" columnSpacing={3} rowSpacing={2}>
            {items}
          </Grid>
        </FormFieldLayout>
      )}
    </DetailsFormSection>
  );
};

export default CostForwardingDetailsSection;
